import {icons, singleMarkerIcons} from './assets'
import translate from 'libs/translate'
import _ from 'lodash'

const SHELL_PROVIDER_NAME = "Shell Recharge"

export function isLocationSupported(cp) {
    if (!cp.authorizationMethods) return false
    return cp.authorizationMethods.indexOf('RFIDToken') !== -1 ||
        cp.authorizationMethods.indexOf('NewMotionApp') !== -1
}

export function getSingleMarkerIcon(cp, selected) {
    const status = cp.status.toLowerCase()
    const isShellMarker = cp.operatorName === SHELL_PROVIDER_NAME

    // TODO: what is 'fast' charging and how do we know cp supports fast charging?
    let markerType
    if (isLocationSupported(cp)) {
        if (isShellMarker) { markerType = 'shell' }
        else { markerType = 'supported' }
    } else {
        markerType = 'unsupported'
    }

    let img = singleMarkerIcons[status][markerType]

    return {
        url: img.src.default,
        scaledSize: {
            height: selected ? img.height * 1.2 : img.height * 0.8,
            width: selected ? img.width * 1.2 : img.width * 0.8,
            heightUnit: 'px',
            widthUnit: 'px'
        }
    }
}


export function toggleMarkerSelection({ marker, data }, toggle) {
    marker.setIcon(getSingleMarkerIcon(data, toggle))
    marker.setZIndex(toggle ? 1000 : 500)
}

function isBlank(str) {
    return _.isUndefined(str) || _.isEmpty(str)
}

function getFullAddress(rawCp) {
    return rawCp.address ? (rawCp.address.streetAndNumber +
        (isBlank(rawCp.address.postalCode) ? '' : ', ' + rawCp.address.postalCode) +
        (isBlank(rawCp.address.city) ? '' : ', ' + rawCp.address.city)) : ''
}

function formatCurrency(currency) {
    return {
        'EUR': '€',
        'GBP': '£',
        'USD': '$',
        'UAH': '₴'
    }[currency] || currency
}

export function getTariffToShow(tariffList) {
    var currency = (!tariffList) ? 'EUR' : tariffList.currency
    var curr = formatCurrency(currency)

    function get(l, p, i) {
        function translationKey(tariffName) {
            return (tariffName === 'startFee') ? 'perSession' : tariffName
        }
        if (i === p.length || !l) {
            return {
                price: translate('landing_sidebar_unknown'),
                label: translate('landing_sidebar_price_' + translationKey(p[0])),
                moreThanOnePrice: false
            }
        }

        if (l[p[i]] !== undefined) {
            return {
                price: curr + ' ' + l[p[i]],
                label: translate('landing_sidebar_price_' + translationKey(p[i])),
                moreThanOnePrice: _.filter(l, function (v) { return v > 0 }).length > 1
            }
        } else return get(l, p, i + 1)
    }

    return get(tariffList, ['perKWh', 'perMinute', 'startFee'], 0)
}

export function prepareLocationDetail(rawLocationData) {
    function formatTariffValue(currency, x) {
        return currency + ' ' + x.toFixed(2)
    }

    function formatTariff(tariff) {
        if (tariff == null) return null

        var result = []
        var currency = formatCurrency(tariff.currency)

        function printPart(tariffPart, text) {
            var tariffValue = tariff[tariffPart]
            var displayableTariff = _.isNumber(tariffValue) && !_.isNaN(tariffValue)
            if (displayableTariff) {
                result.push({
                    str: translate(text),
                    value: formatTariffValue(currency, tariffValue)
                })
            }
        }

        printPart('startFee', 'landing_map_price_start')
        printPart('perKWh', 'landing_map_price_volume')
        printPart('perMinute', tariff.perMinuteMinDuration ? 'landing_map_price_blocking_fee' : 'landing_map_price_time')

        return result
    }

    function formatBlockingFee(tariff) {
        if (!(tariff && tariff.perMinuteMinDuration)) return null

        const currency = formatCurrency(tariff.currency)

        const blockingFeeData = {
            perMinute: formatTariffValue(currency, tariff.perMinute),
            perMinuteMinDuration: tariff.perMinuteMinDuration,
        }

        if (tariff.perMinuteMaxPrice) {
            blockingFeeData.perMinuteMaxPrice = formatTariffValue(currency, tariff.perMinuteMaxPrice)
        }

        return blockingFeeData
    }

    function getTariffDetails(tariff) {
        return {
            fees: formatTariff(tariff),
            blockingFee: formatBlockingFee(tariff)
        }
    }

    function formatStatus(rawStatus) {
        return [
            translate('landing_sidebar_available'),
            translate('landing_sidebar_charging'),
            translate('landing_sidebar_faulted')
        ][rawStatus] || translate('landing_sidebar_unknown')
    }

    function getStatusClass(rawStatus) {
        return ['available', 'charging', 'faulted'][rawStatus] || 'unknown'
    }

    function roundTariffs(originalTariffs) {
        const tariffs = _(originalTariffs)
            .map((tariff, key) => { return [key, tariff] })
            .filter((i) => { return ['startFee', 'perKWh', 'perMinute', 'perMinuteMaxPrice', 'perMinuteMinDuration'].indexOf(i[0]) > -1 })
            .map((i) => {
                return [i[0], parseFloat(i[1].toFixed(2))]
            })
            .fromPairs()
            .value()

        return (originalTariffs && originalTariffs.currency)
            ? _.extend({}, tariffs, { currency: originalTariffs.currency })
            : tariffs
    }

    function mapFromP360Type(originalType) {
        if (originalType === 'FLAT') return 'startFee'
        else if (originalType === 'ENERGY') return 'perKWh'
        else if (originalType === 'MINUTE') return 'perMinute'
        else return originalType // The first release of Pricing will include only ENERGY, more types will be added in the future
    }

    function roundP360Pricing(betaPricing, locationVat) {
        const tariffs = _(betaPricing.tariff.elements[0].priceComponents) // The first release of Pricing will not include complex tariffs, but only tariffs with one element and one component of ENERGY dimension.
            .filter((pc) => { return ['FLAT', 'ENERGY', 'MINUTE'].indexOf(pc.type) > -1 })
            .map((pc) => {
                let vat = pc.vat ? pc.vat : (locationVat ? locationVat : 0)
                let priceWithVat = parseFloat((pc.price * (1 + vat)).toFixed(2))
                return [mapFromP360Type(pc.type), priceWithVat]
            })
            .fromPairs()
            .value()

        return (betaPricing?.tariff?.currency)
            ? _.extend({}, tariffs, { currency: betaPricing.tariff.currency })
            : tariffs
    }

    function statusIndex(status) {
        if (status === 'Available') return 0
        else if (status === 'Occupied') return 1
        else if (status === 'Unavailable') return 2
        else if (status === 'Unknown') return 3
    }

    function getConnectors(rawLocations) {
        return _.flatMap(rawLocations.evses, function (e) {
            return _.map(e.connectors, function (c) {
                return {
                    connectorId: c.externalId,
                    maxPower: c.electricalProperties.maxElectricPower,
                    statusCode: statusIndex(e.status),
                    status: formatStatus(statusIndex(e.status)),
                    statusClass: getStatusClass(statusIndex(e.status)),
                    serial: e.externalId,
                    type: c.connectorType,
                    price: c.betaPricing ? roundP360Pricing(c.betaPricing, rawLocations.vat) : roundTariffs(c.tariff)
                }
            })
        })
    }

    function getStatusAggregation(connectors) {
        var available = '0'
        var isAvailable = _.some(connectors, function (cp) { return cp.statusCode === available })
        var amountPerStatus = _.chain(connectors)
            .groupBy(function (cp) { return cp.statusCode })
            .map(function (group) { return { statusClass: group[0].statusClass, status: group[0].status, amount: group.length } })
            .value()

        var big = (isAvailable)
            ? _.find(amountPerStatus, function (i) { return i.status === available })
            : _.max(amountPerStatus, function (i) { return i.amount })

        var small = _.filter(amountPerStatus, function (i) { return i.status !== big.status })

        return { big: big, small: small }
    }

    function getConnectorGroups(connectors) {
        function getGroupKey(cp) {
            var priceString = _
                .map(cp.price, function (value) { return value })
                .join('-')

            return [cp.type, cp.maxPower, priceString].join('-')
        }

        return _.chain(connectors)
            .groupBy(function (cp) { return getGroupKey(cp) })
            .map(function (group, id) {
                return {
                    groupId: id,
                    type: group[0].type,
                    summary: getStatusAggregation(group),
                    pricingDetails: getTariffDetails(group[0].price),
                    priceToShow: getTariffToShow(group[0].price),
                    power: group[0].maxPower
                }
            })
            .value()
    }

    function groupAccessHours(rawAccessHours) {
        function weekDayToNumber(wd) {
            return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(wd)
        }

        function numberToFullWeekDay(n) {
            return [
                translate('landing_sidebar_monday'),
                translate('landing_sidebar_tuesday'),
                translate('landing_sidebar_wednesday'),
                translate('landing_sidebar_thursday'),
                translate('landing_sidebar_friday'),
                translate('landing_sidebar_saturday'),
                translate('landing_sidebar_sunday')
            ][n]
        }

        function joinWeekDays(wd) {
            if (!wd) return ''
            var first = numberToFullWeekDay(wd[0])
            var last = numberToFullWeekDay(_.last(wd))
            return (wd.length === 1)
                ? first
                : first + ' - ' + last
        }

        function joinRange(i) {
            var bod = '00:00'
            var eod = '23:59'

            if (i.startTime === i.endTime) return 'Closed'
            if (i.startTime === bod && i.endTime === eod) return 'Open'
            return [i.startTime, i.endTime].join('-')
        }

        var byDay = _.chain(rawAccessHours)
            .map(function (i) {
                return _.extend({}, i, {
                    weekDay: weekDayToNumber(i.weekDay),
                    range: joinRange(i)
                })
            })
            .sortBy(function (i) { return i.weekDay })
            .groupBy(function (i) { return i.weekDay })
            .map(function (g, k) {
                return { weekDay: k, range: _.map(g, function (x) { return x.range }).join(' / ') }
            })
            .reduce(function (acc, i) {
                function newItem(ac) {
                    return { weekDays: [ac.weekDay], range: ac.range }
                }

                if (acc.length === 0) {
                    acc.push(newItem(i))
                } else {
                    var last = _.last(acc)
                    if (last.range === i.range) {
                        last.weekDays.push(i.weekDay)
                    } else {
                        acc.push(newItem(i))
                    }
                }

                return acc
            }, [])
            .map(function (i) {
                return { weekDays: joinWeekDays(i.weekDays), range: i.range }
            })
            .value()

        return byDay
    }

    return {
        accessibilityRemark: (rawLocationData.accessibility) ? rawLocationData.accessibility.remark : null,
        unsupported: !isLocationSupported(rawLocationData),
        connectorGroups: getConnectorGroups(getConnectors(rawLocationData)),
        fullAddress: getFullAddress(rawLocationData),
        operator: rawLocationData.operatorName,
        info: rawLocationData.comment,
        code: rawLocationData.externalId,
        access: groupAccessHours(rawLocationData.openingHours)
    }
}

export function isCluster(marker) {
    return marker.markerType === 'MultiLocation'
}

function hasSameId(a, b) {
    return a.id === b.id
}

export function getMarkersToAdd(fetchedMarkers, currentMarkers) {
    const add = _(fetchedMarkers)
        .differenceWith(currentMarkers, hasSameId)
        .value()

    return add
}

export function getMarkersToRemove(fetchedMarkers, currentMarkers) {
    return _(currentMarkers)
        .differenceWith(fetchedMarkers, (a, b) => a.m_id === b.m_id)
        .value()
}
export function getMarkersToKeep(currentMarkers, toRemove) {
    return _(currentMarkers)
        .differenceWith(toRemove, (a, b) => a.m_id === b.m_id)
        .value()
}


export function createMarkerId(marker) {
    if (isCluster(marker)) {
        const lat = marker.coordinates.latitude.toFixed(5)
        const lng = marker.coordinates.longitude.toFixed(5)
        return `${marker.markerType}-${lat}-${lng}-${marker.evseCount}`
    }

    if (marker.markerType === 'SingleLocation') {
        return `${marker.locationUid}-${marker.status}`
    }

    return `?-${Math.random()}`
}

export function addIdToLocations(locations) {
    return _.map(locations, (l) => { return _.extend({}, l, { id: createMarkerId(l) }) })
}

export function createGenericMarker(map, google, position) {
    return new google.maps.Marker({
        position: new google.maps.LatLng(position),
        map
    })
}

export function createMeMarker(map, google, position) {
    return new google.maps.Marker({
        position: new google.maps.LatLng(position),
        icon: icons.meMarker,
        map
    })
}
